// Umpiring Assignments tab — admin's picking board.
//
// Fixed weekend layout: six rows × two columns, grouped by ground
// then day so each row shows the AM and PM game for that
// (ground, day) pair side by side.
//
//   Row 1  Linbrook · Saturday   →  AM | PM
//   Row 2  Linbrook · Sunday     →  AM | PM
//   Row 3  CC-1     · Saturday   →  AM | PM
//   Row 4  CC-1     · Sunday     →  AM | PM
//   Row 5  CC-2     · Saturday   →  AM | PM
//   Row 6  CC-2     · Sunday     →  AM | PM
//
// Each cell is either a rich scoreboard-style match card (when a game
// is scheduled at that slot) or a dashed placeholder naming the slot.
// The View Flyer button opens the printable Assignments Flyer overlay.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardCheck, MapPin, Clock, CheckCircle2, RotateCcw,
  Printer, Trophy, Gavel, ScrollText, Award, Sun, Moon,
  Lock, Unlock, Share2, Send, Phone as PhoneIcon
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import { fmt12, fmtMatchDate } from "../shared";
import AssignmentsFlyer from "./AssignmentsFlyer";
import { buildAnnouncement, shareNode } from "./assignmentsShare";

function initials(u) {
  const f = (u.firstName || "").trim();
  const l = (u.lastName  || "").trim();
  return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "?";
}

const CARD_TONES = ["gold", "cyan", "green", "violet", "orange"];
function nomTone(u) {
  const seed = (u.firstName || "") + (u.lastName || "") + String(u.umpireId || 0);
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return CARD_TONES[h % CARD_TONES.length];
}

function splitDate(iso) {
  if (!iso) return { day: "—", dow: "", mon: "" };
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return { day: iso, dow: "", mon: "" };
  const dow = d.toLocaleDateString("en-US", { weekday: "short" });
  const mon = d.toLocaleDateString("en-US", { month: "short" });
  return { day: String(d.getDate()), dow, mon };
}

// Upcoming weekend's Saturday + Sunday, in the user's local time zone.
// Rules match Upcoming Assignments:
//   - Saturday → today is the Sat, tomorrow is the Sun
//   - Sunday   → yesterday was the Sat, today is the Sun
//   - Other    → next upcoming Sat + Sun
function upcomingWeekendIsos() {
  const now = new Date();
  const dow = now.getDay(); // 0 Sun … 6 Sat
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  let sat;
  if (dow === 6) sat = start;
  else if (dow === 0) sat = new Date(start.getTime() - 86400000);
  else { sat = new Date(start); sat.setDate(start.getDate() + (6 - dow)); }
  const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
  const toIso = d => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  return { satIso: toIso(sat), sunIso: toIso(sun) };
}

// Noon cutoff. Times like "13:30", "9:00 AM", "9 AM" all normalise.
function isAM(time) {
  if (!time) return true; // no time → default to AM slot
  const s = String(time).trim().toUpperCase();
  if (s.includes("PM")) return false;
  if (s.includes("AM")) return true;
  const m = s.match(/^(\d{1,2})/);
  if (!m) return true;
  const h = parseInt(m[1], 10);
  return h < 12;
}

// Grid columns are literal grounds ("Linbrook", "CC-1", "CC-2") — not
// the coarser Linbrook/Clarion rate bucket used elsewhere.
function bucketGroundGrid(g) {
  if (!g) return null;
  const s = String(g).trim().toLowerCase().replace(/\s+/g, "");
  if (s.startsWith("linbrook")) return "Linbrook";
  if (s.startsWith("cc1") || s.startsWith("cc-1")) return "CC-1";
  if (s.startsWith("cc2") || s.startsWith("cc-2")) return "CC-2";
  return null;
}

const AMPM_COLS = ["AM", "PM"];

export default function UmpiringAssignmentsTab() {
  const toast = useToast();
  const [matches,   setMatches]   = useState([]);
  const [nomsById,  setNomsById]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);
  const [sharing,   setSharing]   = useState(false);
  const [posting,   setPosting]   = useState(false);
  const [showFlyer, setShowFlyer] = useState(false);

  // Refs so we can snapshot the exact scope the user asked to share —
  // one for the whole weekend grid, one per (ground, day) row, one per
  // individual match card. Keys are row.key and match.id respectively.
  const gridRef = useRef(null);
  const rowRefs = useRef({});
  const cardRefs = useRef({});
  const setRowRef  = (key, el) => { if (el) rowRefs.current[key]  = el; else delete rowRefs.current[key];  };
  const setCardRef = (id,  el) => { if (el) cardRefs.current[id]  = el; else delete cardRefs.current[id];  };

  async function refresh() {
    setLoading(true);
    try {
      const rows = await api.listUpcomingMatches();
      setMatches(rows);
      const withNoms = rows.filter(m => m.nominatedCount + m.assignedCount > 0);
      const nominations = await Promise.all(
        withNoms.map(m => api.listMatchNominations(m.id).then(list => [m.id, list]))
      );
      setNomsById(Object.fromEntries(nominations));
    } catch (err) {
      toast.error(err.message || "Couldn't load assignments.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function setStatus(matchId, umpireId, status) {
    setBusy(true);
    try {
      await api.setMatchNominationStatus(matchId, umpireId, status);
      const [rows, list] = await Promise.all([
        api.listUpcomingMatches(),
        api.listMatchNominations(matchId)
      ]);
      setMatches(rows);
      setNomsById(m => ({ ...m, [matchId]: list }));
    } catch (err) {
      toast.error(err.message || "Couldn't update assignment.");
    } finally { setBusy(false); }
  }

  async function toggleLock(m, next) {
    setBusy(true);
    try {
      await api.setMatchLocked(m.id, next);
      setMatches(prev => prev.map(row => row.id === m.id ? { ...row, isLocked: next } : row));
      toast.success(next ? "Match locked." : "Match unlocked.");
    } catch (err) {
      toast.error(err.message || "Couldn't update lock.");
    } finally { setBusy(false); }
  }

  // Build the {match, umpires} entries the announcement composer and
  // the share summary both consume. Only assigned umpires make the cut.
  function buildEntries(matchList) {
    const list = matchList.slice().sort((a, b) =>
      (a.date + (a.time || "")).localeCompare(b.date + (b.time || ""))
    );
    return list.map(m => ({
      match: m,
      umpires: (nomsById[m.id] || []).filter(n => n.status === "Assigned")
    }));
  }

  async function postScope(scope, matchList) {
    const entries = buildEntries(matchList);
    if (entries.length === 0) {
      toast.error("Nothing locked to post yet.");
      return;
    }
    setPosting(true);
    try {
      const { title, body } = buildAnnouncement(scope, entries);
      await api.createAnnouncement({
        title,
        body,
        priority: "Normal",
        pinned: false,
        isLeagueOnly: true
      });
      toast.success(entries.length === 1
        ? "Assignment posted to Announcements."
        : `${entries.length} assignments posted to Announcements.`);
    } catch (err) {
      toast.error(err.message || "Couldn't post announcement.");
    } finally { setPosting(false); }
  }

  async function shareScope(scope, node, matchList) {
    if (!node) return;
    const entries = buildEntries(matchList);
    setSharing(true);
    try {
      const summary = `Umpiring Assignments — ${scope}. ${entries.length} game${entries.length === 1 ? "" : "s"}.`;
      const filename = `umpiring-${scope.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
      const r = await shareNode(node, summary, filename);
      if (r.clipboard) toast.info("Card copied to clipboard. Paste anywhere.", { duration: 6000 });
      else if (r.downloaded) toast.info("Card downloaded. Attach it wherever you're sharing.", { duration: 6000 });
    } catch (err) {
      toast.error("Share failed: " + (err.message || err));
    } finally { setSharing(false); }
  }

  const { satIso, sunIso } = useMemo(upcomingWeekendIsos, []);

  const rows = useMemo(() => ([
    { key: "linbrook-sat", ground: "Linbrook", day: "Saturday", date: satIso, tone: "gold"   },
    { key: "linbrook-sun", ground: "Linbrook", day: "Sunday",   date: sunIso, tone: "orange" },
    { key: "cc1-sat",      ground: "CC-1",     day: "Saturday", date: satIso, tone: "cyan"   },
    { key: "cc1-sun",      ground: "CC-1",     day: "Sunday",   date: sunIso, tone: "green"  },
    { key: "cc2-sat",      ground: "CC-2",     day: "Saturday", date: satIso, tone: "violet" },
    { key: "cc2-sun",      ground: "CC-2",     day: "Sunday",   date: sunIso, tone: "red"    }
  ]), [satIso, sunIso]);

  // Bucket matches → row × column.  Rows are (ground, day) pairs and
  // columns are AM / PM slots. Keeping the cell as an array preserves
  // any rare double-booking (two games at the same ground/day/slot)
  // rather than silently dropping one.
  const grid = useMemo(() => {
    const g = {};
    for (const r of rows) {
      g[r.key] = { AM: [], PM: [] };
    }
    for (const m of matches) {
      const col = bucketGroundGrid(m.ground);
      if (!col) continue;
      const ampm = isAM(m.time) ? "AM" : "PM";
      const row = rows.find(r => r.date === m.date && r.ground === col);
      if (!row) continue;
      g[row.key][ampm].push(m);
    }
    return g;
  }, [matches, rows]);

  const totals = useMemo(() => {
    const weekend = matches.filter(m => (m.date === satIso || m.date === sunIso) && bucketGroundGrid(m.ground));
    const withNoms  = weekend.filter(m => (nomsById[m.id] || []).length > 0).length;
    const locked    = weekend.filter(m => m.isLocked).length;
    const fullyDone = weekend.filter(m => m.assignedCount >= 2).length;
    const partly    = weekend.filter(m => m.assignedCount > 0 && m.assignedCount < 2).length;
    return { withNoms, fullyDone, partly, locked, total: weekend.length };
  }, [matches, nomsById, satIso, sunIso]);

  // Flat list of every locked weekend match, ordered day → ground → AM/PM,
  // for the "Post All / Share All" panel-level action.
  const allLockedMatches = useMemo(() => {
    const out = [];
    for (const r of rows) {
      for (const col of AMPM_COLS) {
        for (const m of grid[r.key][col]) {
          if (m.isLocked) out.push(m);
        }
      }
    }
    return out;
  }, [rows, grid]);

  const anyAssigned = matches.some(m => m.assignedCount > 0);

  function renderCell(row, col) {
    const cellMatches = grid[row.key][col];
    const label = col === "AM" ? "AM Game" : "PM Game";
    const Icon  = col === "AM" ? Sun : Moon;
    if (cellMatches.length === 0) {
      return (
        <div key={col} className={`ump-cellSlot ump-cellSlot--${row.tone}`}>
          <div className={`ump-cellSlot-label ump-cellSlot-label--${row.tone}`}>
            <Icon size={12}/> {label}
          </div>
          <div className={`ump-emptySlot ump-emptySlot--${row.tone}`}>
            <span className="ump-emptySlot-ground">{label}</span>
            <span className="ump-emptySlot-note">No game scheduled</span>
          </div>
        </div>
      );
    }
    return (
      <div key={col} className={`ump-cellSlot ump-cellSlot--${row.tone}`}>
        <div className={`ump-cellSlot-label ump-cellSlot-label--${row.tone}`}>
          <Icon size={12}/> {label}
        </div>
        <div className="ump-cellStack">
          {cellMatches.map(m => renderMatchCard(m))}
        </div>
      </div>
    );
  }

  function renderMatchCard(m) {
    const noms      = nomsById[m.id] || [];
    const assigned  = noms.filter(n => n.status === "Assigned");
    const nominated = noms.filter(n => n.status === "Nominated");
    const canAssignMore = assigned.length < 2;
    const isLocked  = !!m.isLocked;
    const canLock   = assigned.length >= 2;
    const { day, dow, mon } = splitDate(m.date);
    const scope = `${m.ground} · ${fmtMatchDate(m.date)}${m.time ? ` · ${fmt12(m.time)}` : ""}`;
    return (
      <article
        key={m.id}
        ref={el => setCardRef(m.id, el)}
        className={`ump-matchCard${isLocked ? " ump-matchCard--locked" : ""}`}
      >
        {isLocked && (
          <span className="ump-matchCard-ribbon">
            <Award size={11}/> Locked In
          </span>
        )}

        <header className="ump-matchCard-head">
          <div className="ump-matchCard-series">
            <Trophy size={11}/> {m.series}
          </div>
          <div className="ump-matchCard-datebox">
            <span className="ump-matchCard-dow">{dow}</span>
            <span className="ump-matchCard-day">{day}</span>
            <span className="ump-matchCard-mon">{mon}</span>
          </div>
        </header>

        <div className="ump-matchCard-teams">
          <span className="ump-matchCard-team">{m.teamOne}</span>
          <span className="ump-matchCard-vs">vs</span>
          <span className="ump-matchCard-team">{m.teamTwo}</span>
        </div>

        <div className="ump-matchCard-meta">
          {m.time && (
            <span><Clock size={12}/> {fmt12(m.time)} ET</span>
          )}
          <span><MapPin size={12}/> {m.ground}</span>
        </div>

        <section className="ump-matchCard-section">
          <header className="ump-matchCard-sectionHead">
            <ScrollText size={12}/>
            <span>Umpires</span>
            <span className={`ump-matchCard-progress${isLocked ? " ump-matchCard-progress--done" : ""}`}>
              {assigned.length}/2 confirmed
            </span>
          </header>
          <div className="ump-slots">
            {[0, 1].map(slot => {
              const u = assigned[slot];
              if (!u) return (
                <div key={slot} className="ump-slot ump-slot--empty">
                  <div className="ump-slot-avatar ump-slot-avatar--empty">?</div>
                  <div className="ump-slot-info">
                    <b>Slot {slot + 1}</b>
                    <small>Pick from nominees</small>
                  </div>
                </div>
              );
              return (
                <div key={slot} className="ump-slot ump-slot--filled">
                  <div className="ump-slot-avatar">{initials(u)}</div>
                  <div className="ump-slot-info">
                    <b>{u.firstName} {u.lastName || ""}</b>
                    <small>{u.teamName || "Umpire"}</small>
                    {isLocked && u.phone && (
                      <span className="ump-slot-phone">
                        <PhoneIcon size={10}/>
                        <a href={`tel:${u.phone}`} onClick={e => e.stopPropagation()}>{u.phone}</a>
                      </span>
                    )}
                  </div>
                  {!isLocked && (
                    <button
                      type="button"
                      className="ump-slot-demote"
                      onClick={() => setStatus(m.id, u.umpireId, "Nominated")}
                      disabled={busy}
                      title="Move back to nominees"
                      aria-label="Unassign umpire"
                    >
                      <RotateCcw size={12}/>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {!isLocked && (
          <section className="ump-matchCard-section">
            <header className="ump-matchCard-sectionHead">
              <Gavel size={12}/>
              <span>Nominees</span>
              <span className="ump-matchCard-progress">
                {nominated.length} available
              </span>
            </header>

            {nominated.length === 0 ? (
              <p className="ump-nominees-empty">
                No pending nominations yet.
              </p>
            ) : (
              <ul className="ump-nominees">
                {nominated.map(n => (
                  <li key={n.id} className={`ump-nominee ump-nominee--${nomTone(n)}`}>
                    <div className="ump-nominee-avatar">{initials(n)}</div>
                    <div className="ump-nominee-info">
                      <b>{n.firstName} {n.lastName || ""}</b>
                      {n.teamName && <small>{n.teamName}</small>}
                    </div>
                    <button
                      type="button"
                      className="ump-nominee-btn"
                      onClick={() => setStatus(m.id, n.umpireId, "Assigned")}
                      disabled={busy || !canAssignMore}
                      title={canAssignMore
                        ? "Confirm this umpire"
                        : "Match is already at 2 umpires — unassign one first"}
                    >
                      <CheckCircle2 size={11}/> Assign
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <footer className="ump-matchCard-actions">
          {isLocked ? (
            <button
              type="button"
              className="ump-cardBtn ump-cardBtn--ghost"
              onClick={() => toggleLock(m, false)}
              disabled={busy}
              title="Unlock this match to change umpires"
            >
              <Unlock size={12}/> Unlock
            </button>
          ) : (
            <button
              type="button"
              className="ump-cardBtn ump-cardBtn--lock"
              onClick={() => toggleLock(m, true)}
              disabled={busy || !canLock}
              title={canLock
                ? "Lock this match — assignments become final"
                : "Assign 2 umpires before locking"}
            >
              <Lock size={12}/> Lock Game
            </button>
          )}
          <button
            type="button"
            className="ump-cardBtn ump-cardBtn--post"
            onClick={() => postScope(scope, [m])}
            disabled={posting || !isLocked}
            title={isLocked
              ? "Post this assignment to Announcements"
              : "Lock the match to post it"}
          >
            <Send size={12}/> Post
          </button>
          <button
            type="button"
            className="ump-cardBtn ump-cardBtn--share"
            onClick={() => shareScope(scope, cardRefs.current[m.id], [m])}
            disabled={sharing || !isLocked}
            title={isLocked
              ? "Share this card via WhatsApp / Messages / Mail…"
              : "Lock the match to share it"}
          >
            <Share2 size={12}/> Share
          </button>
        </footer>
      </article>
    );
  }

  return (
    <>
      <Panel title={
        <div className="ump-assignBoard-heading">
          <ClipboardCheck size={18}/>
          <div>
            <span>Umpiring Assignments</span>
            <small>
              {totals.total} weekend match{totals.total === 1 ? "" : "es"} ·
              {" "}{totals.withNoms} with nominees ·
              {" "}{totals.fullyDone} at 2/2 ·
              {" "}{totals.locked} locked
            </small>
          </div>
          <div className="ump-assignBoard-actions">
            <button
              type="button"
              className="ump-flyerBtn"
              onClick={() => setShowFlyer(true)}
              disabled={!anyAssigned}
              title={anyAssigned ? "View flyer" : "Assign umpires to view the flyer"}
            >
              <Printer size={13}/> View Flyer
            </button>
            <button
              type="button"
              className="ump-flyerBtn ump-flyerBtn--post"
              onClick={() => postScope("This Weekend · All Grounds", allLockedMatches)}
              disabled={posting || allLockedMatches.length === 0}
              title={allLockedMatches.length === 0
                ? "Lock at least one match to post"
                : `Post all ${allLockedMatches.length} locked match(es) to Announcements`}
            >
              <Send size={13}/> Post All
            </button>
            <button
              type="button"
              className="ump-flyerBtn ump-flyerBtn--share"
              onClick={() => shareScope("This Weekend · All Grounds", gridRef.current, allLockedMatches)}
              disabled={sharing || allLockedMatches.length === 0}
              title={allLockedMatches.length === 0
                ? "Lock at least one match to share"
                : "Share all game cards"}
            >
              <Share2 size={13}/> Share All
            </button>
          </div>
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading assignments…</p>}

        {!loading && (
          <div className="ump-assignGrid" ref={gridRef}>
            {rows.map(r => {
              const { day, dow, mon } = splitDate(r.date);
              const rowMatches = [...grid[r.key].AM, ...grid[r.key].PM];
              const rowLocked  = rowMatches.filter(m => m.isLocked);
              const rowScope   = `${r.ground} · ${r.day}`;
              return (
                <section
                  key={r.key}
                  ref={el => setRowRef(r.key, el)}
                  className={`ump-assignRow ump-assignRow--${r.tone}`}
                >
                  <header className={`ump-assignRow-head ump-assignRow-head--${r.tone}`}>
                    <span className="ump-assignRow-icon"><MapPin size={22}/></span>
                    <div className="ump-assignRow-title">
                      <h3>{r.ground} · {r.day}</h3>
                      <small>
                        {dow}, {mon} {day} · AM &amp; PM slots
                      </small>
                    </div>
                    <div className="ump-assignRow-actions">
                      <button
                        type="button"
                        className="ump-rowBtn"
                        onClick={() => postScope(rowScope, rowLocked)}
                        disabled={posting || rowLocked.length === 0}
                        title={rowLocked.length === 0
                          ? "Lock at least one match in this row to post"
                          : `Post ${rowLocked.length} locked game(s) for ${rowScope}`}
                      >
                        <Send size={11}/> Post
                      </button>
                      <button
                        type="button"
                        className="ump-rowBtn"
                        onClick={() => shareScope(rowScope, rowRefs.current[r.key], rowLocked)}
                        disabled={sharing || rowLocked.length === 0}
                        title={rowLocked.length === 0
                          ? "Lock at least one match in this row to share"
                          : `Share the ${rowScope} row card`}
                      >
                        <Share2 size={11}/> Share
                      </button>
                      <span className="ump-assignRow-count">
                        {rowMatches.length} game(s)
                      </span>
                    </div>
                  </header>

                  <div className="ump-assignRow-grid">
                    {AMPM_COLS.map(col => renderCell(r, col))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </Panel>

      {showFlyer && <AssignmentsFlyer onClose={() => setShowFlyer(false)}/>}
    </>
  );
}
